import { action, makeObservable, observable } from 'mobx';
import { LoginForm } from 'modules/membership/forms';
import { BaseViewStore } from 'core/stores';
import { RouterState } from 'mobx-state-router';
import { localStorageHandler } from 'core/services';
import { formInvalidate, isSome } from 'core/utils';
import { loginErrorMessages } from 'common/constants';
import { LoginStore } from 'modules/membership/stores';
import { UserStore } from 'modules/user/stores';
import { ApplicationStore } from 'platform/application/stores';
import { ModalParams } from 'core/models';
import * as packageJSON from '../../../../package.json';

class AppLoginViewStore extends BaseViewStore {
	@observable usernamePasswordAttemptCount = 0; // Wrong username and password attempt count.
	@observable failedLoginAttemptCount = 0; // Wrong general attempt count such as wrong organization or internal error.
	recaptchaKey;

	routes = {
		passwordRecovery: () => this.rootStore.routerStore.goTo('master.app.membership.password-recovery'),
	};

	loginForm = new LoginForm({
		onSuccess: async form => {
			let { app, username, password } = form.values();
			if (!this.loginForm.$('recaptcha').isValid) {
				this.loginForm.invalidate('APPLOGINRECAPTCHA');
			} else {
				const organization = app;
				const applicationIdentifier = app.trim();
				return this.login({
					username: username.trim(),
					password,
					organization,
					app: applicationIdentifier,
				});
			}
		},
		onError: () => {
			if (this.usernamePasswordAttemptCount >= 2 || this.failedLoginAttemptCount >= 2) {
				if (!this.loginForm.$('recaptcha').isValid) {
					this.loginForm.invalidate('APPLOGINRECAPTCHA');
				}
			}
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.loginStore = new LoginStore(rootStore);
		this.userStore = new UserStore(rootStore);
		this.organizationStore = new ApplicationStore(rootStore);
		this.initialize();
	}

	@action async initialize() {
		if (process.env.NODE_ENV == 'production') {
			this.recaptchaKey = '6LeknWkfAAAAAI5FPO0jy53J0Q-lrMgPJxZo_VaS';
		} else {
			this.recaptchaKey = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
		}
		let lastOrg = localStorageHandler.get('lastOrganization');
		let newVersion = packageJSON.version;
		let recaptcha = localStorageHandler.get('recaptcha');
		if (newVersion) {
			if (localStorageHandler.get('appVersion') !== newVersion && recaptcha === null) {
				const timerList = localStorageHandler.get('two-step-authentication-timer');
				const twoStepAuthentication = localStorageHandler.get('two-step-authentication');
				localStorageHandler.clear();
				if (timerList != null) {
					localStorageHandler.set('two-step-authentication-timer', timerList);
				}
				if (twoStepAuthentication != null) {
					localStorageHandler.set('two-step-authentication', twoStepAuthentication);
				}
				localStorageHandler.set('appVersion', newVersion);
			}
		}
		localStorageHandler.remove('newVersion');
		this.loginForm.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.loginForm.reset();
		if (lastOrg && typeof lastOrg === 'string') {
			this.loginForm.$('app').set('value', lastOrg);
			localStorageHandler.set('lastOrganization', lastOrg);
		}
		if (recaptcha) {
			this.usernamePasswordAttemptCount = recaptcha.usernamePasswordAttemptCount;
			this.failedLoginAttemptCount = recaptcha.loginAttemptCount;
			this.checkLoginAttempt();
		}
	}

	@action.bound
	login = async ({ username, password, organization }) => {
		this.suspend();
		try {
			const response = await this.loginStore.login({ username, password, organization, options: 'sliding' });
			// Get and set current organization info into root store.
			await this.organizationStore.getOrganizationInfo();
			this.rootStore.applicationStore.setOrganization(this.organizationStore.item);
			await this.userStore.findByUserName(username, { embed: ['roles'] });
			if (this.userStore.response.roles.length === 0) {
				this.rootStore.authStore.setSignInRedirect(new RouterState('master.app.main.order.list'));
			} else {
				this.rootStore.authStore.setSignInRedirect(
					new RouterState(isSome(this.userStore.response.roles[0].homePage))
						? this.userStore.response.roles[0].homePage
						: 'master.app.main.user.list'
				);
			}
			localStorageHandler.set('lastOrganization', this.organizationStore.item);
			localStorageHandler.remove('password-change-user');
			this.rootStore.routerStore.goTo(this.rootStore.authStore.getSignInRedirect());
			this.rootStore.viewStore.countUnlinkedOrderLines();
			if (response.data.passwordExpirationDays <= 5 && response.data.passwordExpirationDays >= 1) {
				const message =
					response.data.passwordExpirationDays === 1
						? `${response.data.passwordExpirationDays} day`
						: `${response.data.passwordExpirationDays} days`;
				this.rootStore.notificationStore.warning(['LOGINFORM.PASSWORDEXPIRES', { value: message }]);
			}
		} catch (err) {
			const { statusCode, data } = err;
			this.failedLoginAttemptCount++;
			if (statusCode === 400 && data && data.error) {
				if (data.error === loginErrorMessages.invalidOrganizationIdentifier) {
					// Invalid organization.
					if (this.usernamePasswordAttemptCount >= 2 || this.failedLoginAttemptCount >= 2) {
						let recaptcha = {
							loginAttemptCount: this.failedLoginAttemptCount,
							usernamePasswordAttemptCount: this.usernamePasswordAttemptCount,
						};
						localStorageHandler.set('recaptcha', recaptcha);
						this.loginForm.$('recaptcha').set('rules', 'required');
					}
					this.loginForm.invalidate('APPLOGINORGINVALID');
				} else if (data.error == loginErrorMessages.organizationLocked) {
					this.loginForm.invalidate('APPLOGINORGDEACTIVATED');
				} else if (data.error === loginErrorMessages.invalidGrantLock) {
					this.loginForm.invalidate('INVALID_GRANT_LOCK');
				} else if (data.error === loginErrorMessages.invalidGrantApprove) {
					this.loginForm.invalidate('INVALID_GRANT_APPROVE');
				} else if (data.error === loginErrorMessages.invalidGrant) {
					// Invalid username or password.
					this.loginForm.invalidate('APPLOGININVALID');
					this.usernamePasswordAttemptCount = data.loginAttemptCount;
					if (this.usernamePasswordAttemptCount >= 2 || this.failedLoginAttemptCount >= 2) {
						let recaptcha = {
							loginAttemptCount: this.failedLoginAttemptCount,
							usernamePasswordAttemptCount: this.usernamePasswordAttemptCount,
						};
						localStorageHandler.set('recaptcha', recaptcha);
						this.loginForm.$('recaptcha').set('rules', 'required');
					}
				}
			} else if (statusCode === 403 && data.error_description == 'password_expired') {
				// Requires password reset.
				this.rootStore.notificationStore.error(data.error);
				localStorageHandler.set('password-change-username', encodeURIComponent(`${username}-${data.organization_id}`));
				this.rootStore.routerStore.goTo('master.app.membership.password-change');
			} else if (statusCode === 403 && data.error_description === 'requires_two_step_authentication') {
				// Requires two step authentication.
				localStorageHandler.set('two-step-authentication', {
					userName: encodeURIComponent(`${username}-${data.organization_id}`),
					userId: data.user_id,
					phoneNumber: data.user_phone,
					verificationCodeSent: false,
				});
				this.rootStore.routerStore.goTo('master.app.membership.two-step-authentication');
			} else {
				this.checkLoginAttempt();
				formInvalidate({ form: this.loginForm, err });
			}
		}
		this.resume();
	};

	@action.bound onRecaptchaChange(token) {
		// Token will be valid for 2 minutes.
		this.loginForm.$('recaptcha').set('value', token);
	}

	@action.bound onRecaptchaExpired() {
		this.loginForm.$('recaptcha').set('value', null);
	}

	@action.bound checkLoginAttempt() {
		if (this.usernamePasswordAttemptCount >= 2 || this.failedLoginAttemptCount >= 2) {
			this.loginForm.$('recaptcha').set('rules', 'required');
		}
	}

	privacyPolicyModal = new ModalParams();
}

export default AppLoginViewStore;



// WEBPACK FOOTER //
// ./src/modules/membership/stores/AppLoginViewStore.js